import { motion, useInView } from 'motion/react';
import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Users, Calendar, TrendingUp, Handshake, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { Button } from './Button';
import { IconCard } from './IconCard';
import { isFinnishPage } from '../../lib/blogPaths';

// { value: '30+', end: 30, suffix: '+' }, etc.
const statsEn = [
  { icon: Users, value: '30+', end: 30, suffix: '+', label: 'Team Members' },
  { icon: Calendar, value: '2018', end: 2018, suffix: '', label: 'Founded' },
  { icon: TrendingUp, value: '100%', end: 100, suffix: '%', label: 'AI-Native' },
  { icon: Handshake, value: '100%', end: 100, suffix: '%', label: 'Employee-Owned' },
];
const statsFi = [
  { icon: Users, value: '30+', end: 30, suffix: '+', label: 'Tiimin jäsentä' },
  { icon: Calendar, value: '2018', end: 2018, suffix: '', label: 'Perustettu' },
  { icon: TrendingUp, value: '100%', end: 100, suffix: '%', label: 'AI-natiivi' },
  { icon: Handshake, value: '100%', end: 100, suffix: '%', label: 'Työntekijäomistus' },
];

function useCountUp(end, enabled, duration = 1500) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      setCurrent(Math.round(eased * end));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [enabled, end, duration]);
  return current;
}

function StatCard({ stat, index, isInView }) {
  const Icon = stat.icon;
  const count = useCountUp(stat.end, isInView, stat.end > 1000 ? 2000 : 1500);
  const display = stat.end > 1000 ? count : `${count}${stat.suffix}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: 0.2 + index * 0.06 }}
      className="relative z-10 bg-white rounded-2xl border border-gray-200 p-6 h-full hover:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)] transition-shadow group"
    >
      <IconCard Icon={Icon} className="mb-4" />
      <p className="text-2xl font-bold text-gray-900 mb-1 font-display">{display}</p>
      <p className="text-sm text-gray-500 leading-relaxed">{stat.label}</p>
    </motion.div>
  );
}

export function TeamCultureSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const stats = isFinnish ? statsFi : statsEn;
  const [teamImageSrc, setTeamImageSrc] = useState('/team/team-culture.png');

  const heading = isFinnish ? 'Identiolaisten rakentama' : 'Built by Identians';
  const body = isFinnish
    ? '30+ intohimoista ammattilaista: yli 20 kehittäjää, asiantuntevia suunnittelijoita ja omistautunut tukitiimi, jotka rakastavat monimutkaisten ongelmien ratkaisemista. Toimimme Turussa ja Helsingissä, asiakkaiden kanssa ympäri Suomea ja sen ulkopuolella.'
    : "30+ passionate professionals: 20+ developers, expert designers, and dedicated support team who love solving complex problems. We're based in Turku and Helsinki, working with clients across Finland and beyond.";
  const joinCta = isFinnish ? 'Liity tiimiimme' : 'Join Our Team';
  const meetCta = isFinnish ? 'Tutustu tiimiin' : 'Meet the Team';
  const meetHref = isFinnish ? '/tiimi' : '/team';

  return (
    <section ref={ref} className="section-rhythm bg-white [content-visibility:auto]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="aspect-[4/3] max-h-[400px] rounded-2xl overflow-hidden bg-gray-100 shadow-lg relative">
              <Image
                src={teamImageSrc}
                alt={isFinnish ? 'Identio-tiimi työssä' : 'Identio team at work'}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
                onError={() => setTeamImageSrc('/team-at-work.png')}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="order-1 lg:order-2"
          >
            <h2 className="heading-2 mb-4 text-gray-900">{heading}</h2>
            <p className="text-lg text-gray-500 leading-relaxed mb-8">
              {body}
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <StatCard
                    key={stat.label}
                    stat={stat}
                    index={index}
                    isInView={isInView}
                  />
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <Button href={isFinnish ? '/rekry' : '/careers'}>
                {joinCta}
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Link
                href={meetHref}
                className="group inline-flex items-center gap-2 text-brand font-semibold hover:text-brand-hover hover:gap-3 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded sm:ml-6 touch-manipulation"
              >
                {meetCta}
                <ArrowRight className="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
